import type { FC, ReactNode } from 'react'
import type { NumberFormat } from '@/lib/format'
import type { DevicePose } from '@/components/three/device-constants'
import type { TargetResolver } from '@/components/three/device-canvas'

import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatValue } from '@/lib/format'
import { gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { Hero } from '@/components/landing/hero'
import { DeviceFallback } from '@/components/three/device-fallback'

import {
    clamp01,
    fitWidth,
    lerp,
    samplePose,
    smoothstep
} from '@/components/three/device-constants'

import {
    StoryCashFlowCard,
    StoryCategoriesCard,
    StoryExpensesCard,
    StoryIncomeCard,
    StoryInsightCard,
    StoryPerformanceCard,
    StoryRevenueCard
} from '@/components/landing/story-panels'

const DeviceCanvas = dynamic(
    () => import('@/components/three/device-canvas').then(module => module.DeviceCanvas),
    { ssr: false }
)

/**
 * Device poses along the scroll. Phase 0 to 1 covers the hero scrolling
 * away, 1 to 2 covers the pinned story where the dashboard assembles.
 */
const KEYFRAMES: Array<{ at: number } & DevicePose> = [
    { at: 0, rotationX: 0.3, rotationY: -0.62, rotationZ: 0.07, depth: 0, scale: 1, idle: 1, shadow: 1 },
    { at: 0.55, rotationX: 0.1, rotationY: -1.3, rotationZ: 0.02, depth: 0.25, scale: 1.04, idle: 0.35, shadow: 0.4 },
    { at: 1, rotationX: -0.05, rotationY: -0.2, rotationZ: 0, depth: 0.35, scale: 1.06, idle: 0.3, shadow: 0 },
    { at: 1.4, rotationX: 0.1, rotationY: 0.38, rotationZ: -0.02, depth: 0.2, scale: 1, idle: 0.3, shadow: 0 },
    { at: 1.75, rotationX: 0.2, rotationY: -0.25, rotationZ: 0.03, depth: -0.2, scale: 0.94, idle: 0.3, shadow: 0 },
    { at: 2, rotationX: 0.28, rotationY: 0.3, rotationZ: 0.05, depth: -0.6, scale: 0.88, idle: 0.3, shadow: 0 }
]

interface LayerProps {
    depth: 'back' | 'front'
    className?: string
    children: ReactNode
}

/** Positions a story card; the inner element is what the timeline animates. */
const StoryLayer: FC<LayerProps> = ({ depth, className, children }): ReactNode => {
    return (
        <div className={cn('absolute [perspective:1200px]', className)}>
            <div data-story-panel={depth} className='will-change-transform'>
                {children}
            </div>
        </div>
    )
}

export const Experience: FC = (): ReactNode => {
    const experienceRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const heroRef = useRef<HTMLElement>(null)
    const heroSlotRef = useRef<HTMLDivElement>(null)
    const storyRef = useRef<HTMLElement>(null)
    const storySlotRef = useRef<HTMLDivElement>(null)
    const [fallback, setFallback] = useState(false)
    const reducedMotion = useReducedMotion()

    // Last mobile device height, eased toward its target each frame so it glides in.
    const mobileY = useRef<number | null>(null)

    const resolver = useRef<TargetResolver>((canvas, reducedMotion) => {
        const hero = heroRef.current
        const heroSlot = heroSlotRef.current
        const story = storyRef.current
        const storySlot = storySlotRef.current

        if (!hero || !heroSlot || !story || !storySlot) return null

        const canvasRect = canvas.getBoundingClientRect()
        const heroRect = hero.getBoundingClientRect()
        const heroSlotRect = heroSlot.getBoundingClientRect()
        const storyRect = story.getBoundingClientRect()
        const storySlotRect = storySlot.getBoundingClientRect()

        const heroProgress = clamp01((canvasRect.top - heroRect.top) / Math.max(1, heroRect.height))
        const storyProgress = clamp01((canvasRect.top - storyRect.top) / Math.max(1, storyRect.height - canvasRect.height))
        const blend = reducedMotion ? 0 : smoothstep(heroProgress)
        const pose = samplePose(KEYFRAMES, reducedMotion ? 0 : heroProgress + storyProgress)

        const heroX = heroSlotRect.left + heroSlotRect.width / 2 - canvasRect.left
        const heroY = heroSlotRect.top + heroSlotRect.height / 2 - canvasRect.top
        const storyX = storySlotRect.left + storySlotRect.width / 2 - canvasRect.left
        const storyY = storySlotRect.top + storySlotRect.height / 2 - canvasRect.top

        // On phones and tablets the hero is taller than the screen, so flying the
        // device across it would pass behind the intro text and widgets. Instead it
        // scrolls away with its hero slot, then rises in beneath the hero and
        // settles into the story slot.
        if (canvasRect.width < 1024 && !reducedMotion) {
            const followHero = heroSlotRect.bottom > canvasRect.top
            const risingY = heroRect.bottom - canvasRect.top + storySlotRect.height / 2 + 24

            const targetY = followHero ? heroY : Math.max(storyY, risingY)
            const previousY = mobileY.current

            // Track the hero exactly while it is on screen, then ease (about 6% per
            // frame) so the device settles slowly instead of snapping into place.
            const y = followHero || previousY === null ? targetY : previousY + (targetY - previousY) * 0.06

            mobileY.current = y

            return {
                x: followHero ? heroX : storyX,
                y,
                width: followHero ? fitWidth(heroSlotRect) : fitWidth(storySlotRect, 1),
                rotationX: pose.rotationX,
                rotationY: pose.rotationY,
                rotationZ: pose.rotationZ,
                depth: pose.depth,
                scale: pose.scale,
                idle: pose.idle,
                shadow: followHero ? pose.shadow : 0
            }
        }

        return {
            x: lerp(heroX, storyX, blend),
            y: lerp(heroY, storyY, blend),
            width: lerp(fitWidth(heroSlotRect), fitWidth(storySlotRect, 1), blend),
            rotationX: pose.rotationX,
            rotationY: pose.rotationY,
            rotationZ: pose.rotationZ,
            depth: pose.depth,
            scale: pose.scale,
            idle: pose.idle,
            shadow: pose.shadow
        }
    })

    const showFallback = useCallback(() => setFallback(true), [])

    useEffect(() => {
        if (!document.fonts) return

        let cancelled = false

        document.fonts.ready.then(() => {
            if (!cancelled) ScrollTrigger.refresh()
        })

        return () => {
            cancelled = true
        }
    }, [])

    useGSAP(() => {
        const stage = stageRef.current
        const story = storyRef.current

        if (!stage || !story) return

        ScrollTrigger.create({
            trigger: story,
            start: 'top 40%',
            end: 'bottom top',
            onToggle: self => {
                const devices = stage.querySelectorAll('[data-story-device]')

                if (devices.length) gsap.set(devices, { autoAlpha: self.isActive ? 1 : 0 })
            }
        })

        const media = gsap.matchMedia()

        media.add('(prefers-reduced-motion: no-preference)', () => {
            const select = (selector: string) => Array.from(stage.querySelectorAll<HTMLElement>(selector))
            const copy = select('[data-story-copy]')
            const back = select('[data-story-panel="back"]')
            const front = select('[data-story-panel="front"]')
            const wipes = select('[data-chart-wipe]')
            const bars = select('[data-bar]')
            const arcs = select('[data-arc]')
            const progress = select('[data-progress]')
            const counts = select('[data-count]')

            gsap.set([...wipes, ...bars, ...arcs, ...progress], { transition: 'none' })

            const timeline = gsap.timeline({
                defaults: { ease: 'power3.out' },
                scrollTrigger: {
                    trigger: story,
                    start: 'top 40%',
                    end: 'bottom bottom',
                    scrub: 0.9,
                    invalidateOnRefresh: true
                }
            })

            timeline.fromTo(copy, { autoAlpha: 0, y: 48 }, { autoAlpha: 1, y: 0, duration: 1, stagger: 0.12 }, 0)
            timeline.fromTo(back, { autoAlpha: 0, z: -420, y: 90, rotationX: 16 }, { autoAlpha: 1, z: 0, y: 0, rotationX: 0, duration: 1.5, stagger: 0.22 }, 0.55)
            timeline.fromTo(front, { autoAlpha: 0, z: 260, y: 130, rotationX: -10 }, { autoAlpha: 1, z: 0, y: 0, rotationX: 0, duration: 1.5, stagger: 0.24 }, 1)

            timeline.fromTo(wipes, { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, stagger: 0.12, ease: 'power2.inOut' }, 1.5)
            timeline.fromTo(bars, { scaleY: 0, transformOrigin: '50% 100%' }, { scaleY: 1, duration: 1, stagger: 0.025 }, 1.4)
            timeline.fromTo(progress, { scaleX: 0, transformOrigin: '0% 50%' }, { scaleX: 1, duration: 1.2, stagger: 0.08 }, 1.7)

            arcs.forEach((arc, index) => {
                const finalDash = arc.style.getPropertyValue('--arc-dash')
                const circumference = arc.style.getPropertyValue('--arc-circ') || '264'

                timeline.fromTo(arc, { strokeDasharray: `0 ${circumference}` }, { strokeDasharray: finalDash, duration: 1.3 }, 1.6 + index * 0.1)
            })

            counts.forEach(element => {
                const target = Number(element.dataset.count ?? 0)
                const format = (element.dataset.format ?? 'number') as NumberFormat
                const decimals = element.dataset.decimals ? Number(element.dataset.decimals) : undefined
                const counter = { value: 0 }

                element.textContent = formatValue(0, format, decimals)

                timeline.to(counter, {
                    value: target,
                    duration: 1.5,
                    ease: 'power2.out',
                    onUpdate: () => {
                        element.textContent = formatValue(counter.value, format, decimals)
                    }
                }, 1.4)
            })

            timeline.to(back, { y: -36, duration: 2.2, stagger: 0.04, ease: 'none' }, 3.1)
            timeline.to(front, { y: -70, duration: 2.2, stagger: 0.04, ease: 'none' }, 3.1)
            timeline.to(copy, { y: -24, duration: 2.2, ease: 'none' }, 3.1)

            return () => {
                counts.forEach(element => {
                    const format = (element.dataset.format ?? 'number') as NumberFormat
                    const decimals = element.dataset.decimals ? Number(element.dataset.decimals) : undefined

                    element.textContent = formatValue(Number(element.dataset.count ?? 0), format, decimals)
                })
            }
        })

        media.add('(prefers-reduced-motion: reduce)', () => {
            ScrollTrigger.create({
                trigger: story,
                start: 'top 40%',
                end: 'bottom top',
                onToggle: self => {
                    gsap.set(stage.querySelectorAll('[data-story-panel], [data-story-copy]'), { autoAlpha: self.isActive ? 1 : 0 })
                }
            })
        })

        return () => media.revert()
    }, { scope: stageRef })

    return (
        <div ref={experienceRef} id='experience' className='relative isolate'>
            <div
                ref={stageRef}
                aria-hidden='true'
                className='story-stage pointer-events-none sticky top-0 z-10 h-[100svh] w-full overflow-hidden'
            >
                <div className='absolute inset-0 z-0'>
                    <StoryLayer depth='back' className='left-[5%] top-[25%] hidden w-[clamp(240px,20vw,300px)] md:block'>
                        <StoryRevenueCard />
                    </StoryLayer>

                    <StoryLayer depth='back' className='right-[5%] top-[25%] hidden w-[clamp(240px,19vw,290px)] md:block'>
                        <StoryCategoriesCard />
                    </StoryLayer>

                    <StoryLayer depth='back' className='bottom-[9%] left-[7%] hidden w-[clamp(230px,18vw,270px)] [@media(min-width:1024px)_and_(min-height:780px)]:block'>
                        <StoryCashFlowCard />
                    </StoryLayer>

                    <StoryLayer depth='back' className='bottom-[8%] right-[7%] hidden w-[clamp(230px,18vw,270px)] [@media(min-width:1024px)_and_(min-height:780px)]:block'>
                        <StoryPerformanceCard />
                    </StoryLayer>
                </div>

                <div className='absolute inset-0 z-10'>
                    {!fallback && (
                        <DeviceCanvas resolver={resolver} observe={experienceRef} onFallback={showFallback} />
                    )}

                    {(fallback || reducedMotion) && (
                        <div
                            data-story-device=''
                            className='absolute left-1/2 top-[57%] w-[56vw] max-w-[400px] -translate-x-1/2 -translate-y-1/2 md:top-[54%] md:w-[27vw]'
                        >
                            <DeviceFallback />
                        </div>
                    )}
                </div>

                <div className='absolute inset-0 z-20'>
                    <div className='absolute inset-x-0 top-[11%] px-5 text-center md:top-[12%]'>
                        <p data-story-copy='' className='font-mono text-[10.5px] font-medium uppercase tracking-[0.2em] text-stone-dark'>
                            One living view
                        </p>

                        <p data-story-copy='' className='mx-auto mt-4 max-w-3xl font-heading text-display-md font-medium text-burgundy'>
                            Every number, in <span className='text-stone'>one calm place.</span>
                        </p>
                    </div>

                    <StoryLayer depth='front' className='left-[4%] top-[29%] w-[44%] md:left-[13%] md:top-[55%] md:w-[clamp(210px,18vw,250px)]'>
                        <StoryIncomeCard />
                    </StoryLayer>

                    <StoryLayer depth='front' className='right-[4%] top-[29%] w-[44%] md:right-[13%] md:top-[53%] md:w-[clamp(210px,18vw,250px)]'>
                        <StoryExpensesCard />
                    </StoryLayer>

                    <StoryLayer depth='front' className='bottom-[6%] left-[4%] right-[4%] md:left-1/2 md:right-auto md:w-[380px] md:-translate-x-1/2'>
                        <StoryInsightCard />
                    </StoryLayer>
                </div>

                <div
                    ref={storySlotRef}
                    className='absolute left-1/2 top-[57%] aspect-[3/2] w-[62vw] max-w-[440px] -translate-x-1/2 -translate-y-1/2 md:top-[54%] md:w-[30vw]'
                />
            </div>

            <Hero sectionRef={heroRef} slotRef={heroSlotRef} fallback={fallback} />

            <section ref={storyRef} id='story' aria-labelledby='story-title' className='relative h-[230svh] md:h-[300svh]'>
                <div className='sr-only'>
                    <h2 id='story-title'>Every number, in one calm place</h2>

                    <p>
                        Veyra brings income, expenses, revenue, spending categories, cash flow, performance indicators and AI insights together in a single view that updates in real time.
                    </p>
                </div>
            </section>
        </div>
    )
}
